import React from 'react';
import { Box, Button, Container, Paper, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { defineMessages, useIntl } from 'react-intl';
import { useQueryClient, useMutation } from 'react-query';
import PasswordStrengthBar from 'react-password-strength-bar';
import API, { useAuth, useCsrfToken } from '@wheeebo/polaris-api';
import { useSnackbar } from '@wheeebo/polaris-snackbar';
import { ConfirmationDialog } from '../dialog';
import { useErrorHandler } from '../errorHandler';
import { ValidatableField } from '../form';
import Head from './Head';
const msgs = defineMessages({
    errorNoUser: {
        id: 'pages.member.PasswordUpdatePage.error.noUesr',
        defaultMessage: 'user not found',
    },
    errorInvalidValue: {
        id: 'pages.member.PasswordUpdatePage.error.invalidValue',
        defaultMessage: 'invalid value found',
    },
    errorInvalidEmail: {
        id: 'pages.member.PasswordUpdatePage.error.invalidEmail',
        defaultMessage: 'invalid email',
    },
    headerTitle: {
        id: 'pages.member.PasswordUpdatePage.header.title',
        defaultMessage: 'Change Password',
    },
    textContent: {
        id: 'pages.member.PasswordUpdatePage.text.content',
        defaultMessage: 'Please enter your current email and password, and the new password.',
    },
    inputEmailTitle: {
        id: 'pages.member.PasswordUpdatePage.input.emailTitle',
        defaultMessage: 'Email',
    },
    inputPasswordTitle: {
        id: 'pages.member.PasswordUpdatePage.input.passwordTitle',
        defaultMessage: 'Password',
    },
    inputNewPasswordTitle: {
        id: 'pages.member.PasswordUpdatePage.input.newPasswordTitle',
        defaultMessage: 'New Password',
    },
    inputPasswordPlaceholder: {
        id: 'pages.member.PasswordUpdatePage.input.passwordPlaceholder',
        defaultMessage: 'Enter your current password',
    },
    inputNewPasswordPlaceholder: {
        id: 'pages.member.PasswordUpdatePage.input.newPasswordPlaceholder',
        defaultMessage: 'Enter your new password',
    },
    buttonCancel: {
        id: 'pages.member.PasswordUpdatePage.button.cancel',
        defaultMessage: 'Cancel',
    },
    buttonChangePassword: {
        id: 'pages.member.PasswordUpdatePage.button.changePassword',
        defaultMessage: 'Change Password',
    },
    dialogConfirmationTitle: {
        id: 'pages.member.PasswordUpdatePage.dialog.confirmationTitle',
        defaultMessage: 'Confirm',
    },
    dialogConfirmationContent: {
        id: 'pages.member.PasswordUpdatePage.dialog.confirmationContent',
        defaultMessage: 'Are you really going to change your password? After the change request is submitted, ' +
            'your account is invalidated and redirected to the login page. Please sign in with ' +
            'your new password.',
    },
});
function PasswordUpdatePage() {
    const intl = useIntl();
    const { csrfToken } = useCsrfToken();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { account } = useAuth();
    const { showSuccess } = useSnackbar();
    const { register, formState, handleSubmit: validate } = useForm({ mode: 'onChange' });
    const { errors } = formState;
    const { showAndEmitJsErrorEvent, showError } = useErrorHandler();
    const [email, setEmail] = React.useState();
    const [password, setPassword] = React.useState();
    const [newPassword, setNewPassword] = React.useState();
    const [strongPassword, setStrongPassword] = React.useState(false);
    const [confirmationDialogOpen, setConfirmationDialogOpen] = React.useState(false);
    const { mutate: updateAccount, isLoading } = useMutation((props) => API.Accounts.update(csrfToken, props), {
        onSuccess: () => {
            queryClient.invalidateQueries(API.Accounts.CacheKey);
            showSuccess('Your password was successfully updated');
            navigate('/settings');
        },
        onError: showAndEmitJsErrorEvent,
    });
    const handleGoBack = React.useCallback(() => navigate('/settings'), [navigate]);
    const handleSubmit = React.useCallback(() => {
        if (!account) {
            showError(intl.formatMessage(msgs.errorNoUser));
            return;
        }
        if (!email || !password || !newPassword) {
            showError(intl.formatMessage(msgs.errorInvalidValue));
            return;
        }
        setConfirmationDialogOpen(true);
    }, [email, password, newPassword, showError, intl, , account]);
    const handleConfirmationCancel = React.useCallback(() => setConfirmationDialogOpen(false), []);
    const handleConfirmationOk = React.useCallback(async () => {
        setConfirmationDialogOpen(false);
        if (!account) {
            showError(intl.formatMessage(msgs.errorNoUser));
            return;
        }
        if (!email || !password || !newPassword) {
            showError(intl.formatMessage(msgs.errorInvalidValue));
            return;
        }
        updateAccount({ email, password, newPassword });
    }, [email, password, newPassword, showError, intl, account, updateAccount]);
    return (React.createElement(Container, { sx: { display: 'flex', justifyContent: 'center' } },
        React.createElement(Head, { title: intl.formatMessage(msgs.headerTitle) }),
        React.createElement("form", { onSubmit: validate(handleSubmit) },
            React.createElement(Paper, { sx: { width: 400, mt: 8, p: 3 } },
                React.createElement(Stack, { gap: 3 },
                    React.createElement(Typography, { align: "center", variant: "h6" }, intl.formatMessage(msgs.headerTitle)),
                    intl.formatMessage(msgs.textContent),
                    React.createElement(ValidatableField, { id: "email", autoComplete: "email", type: "email", label: intl.formatMessage(msgs.inputEmailTitle), fullWidth: true, disabled: isLoading, errors, register, registerOptions: {
                            required: true,
                            pattern: /\S+@\S+\.\S+/,
                            maxLength: 128,
                            onChange: (e) => setEmail(e.target.value),
                        } }),
                    React.createElement(ValidatableField, { id: "password", autoComplete: "current-password", label: intl.formatMessage(msgs.inputPasswordTitle), placeholder: intl.formatMessage(msgs.inputPasswordPlaceholder), fullWidth: true, type: "password", disabled: isLoading, errors, register, registerOptions: {
                            required: true,
                            maxLength: 128,
                            onChange: (e) => setPassword(e.target.value),
                        } }),
                    React.createElement(Box, null,
                        React.createElement(ValidatableField, { id: "newPassword", autoComplete: "new-password", label: intl.formatMessage(msgs.inputNewPasswordTitle), placeholder: intl.formatMessage(msgs.inputNewPasswordPlaceholder), fullWidth: true, type: "password", disabled: isLoading, errors, register, registerOptions: {
                                required: true,
                                maxLength: 128,
                                onChange: (e) => setNewPassword(e.target.value),
                            } }),
                        React.createElement(PasswordStrengthBar, { password: newPassword, onChangeScore: (score) => setStrongPassword(score === 4) })),
                    React.createElement(Stack, { direction: "row", gap: 1 },
                        React.createElement(Button, { disabled: isLoading, fullWidth: true, onClick: handleGoBack, size: "medium" }, intl.formatMessage(msgs.buttonCancel)),
                        React.createElement(Button, { fullWidth: true, disabled: isLoading || !email || !password || !newPassword || !strongPassword, type: "submit", size: "medium" }, intl.formatMessage(msgs.buttonChangePassword)))))),
        React.createElement(ConfirmationDialog, { open: confirmationDialogOpen, title: intl.formatMessage(msgs.dialogConfirmationTitle), description: intl.formatMessage(msgs.dialogConfirmationContent), onCancel: handleConfirmationCancel, onConfirm: handleConfirmationOk })));
}
export default React.memo(PasswordUpdatePage);
